import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

const BG = "https://images.unsplash.com/photo-1721292175149-8a929b7635f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxvcmdhbmljJTIwZmFybSUyMHN1bnJpc2V8ZW58MHx8fHwxNzg2NzcwMTMwfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [msEnabled, setMsEnabled] = useState(false);

  useEffect(() => {
    api.get("/auth/config").then((r) => setMsEnabled(r.data.microsoft_enabled)).catch(() => {});
    if (params.get("error")) setError("Microsoft sign-in could not be completed. Please try again or use email & password.");
    if (params.get("error") === "ms_no_access") setError("This Microsoft account does not have access to the GirOrganic NDR panel. Please contact your admin to be added.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="Organic farm at sunrise" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0A2E2E]/55" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-4xl font-extrabold tracking-tight leading-tight">
            Track every delayed<br />shipment to resolution.
          </h2>
          <p className="mt-4 text-white/85 max-w-md">
            GirOrganic NDR Control Center — monitor ClickPost non-delivery reports, request reattempts & RTOs,
            and never miss a resolution deadline.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm fade-up">
          <div className="flex items-center gap-2 mb-8">
            <img src="/logo.png" alt="GirOrganic" className="h-11 w-11 rounded-full object-cover" />
            <div>
              <p className="font-heading font-extrabold text-lg text-[#111411] leading-none">GirOrganic</p>
              <p className="text-xs text-muted-foreground mt-0.5">NDR Control Center</p>
            </div>
          </div>

          <h1 className="font-heading text-2xl font-bold text-[#111411]">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Sign in to manage your NDR shipments.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email}
                     onChange={(e) => setEmail(e.target.value)} placeholder="you@girorganic.com" required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password-input" value={password}
                     onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="mt-1.5" />
            </div>
            {error && <p className="text-sm text-[#C24127]" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
                    className="w-full bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white transition-colors duration-200">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Sign in
            </Button>
          </form>

          {msEnabled && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <Button type="button" variant="outline" data-testid="microsoft-login-button"
                      onClick={() => { window.location.href = `${window.location.origin}/api/auth/microsoft/login`; }}
                      className="w-full gap-2">
                <MsIcon /> Continue with Microsoft
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}
